import { FieldValues, Path, RegisterOptions } from 'react-hook-form';
import {
  EMAIL_MAX_LENGTH,
  NATIONAL_IDENTIFICATION_NUMBER_MAX_LENGTH,
  ORGANISATION_NAME_MAX_LENGTH
} from '~/App/shared/validation/constants';
import {
  regexEmail,
  regexOrganisationNumber,
  regexSwedishZipCode,
  regexZipCode
} from '~/helpers/regex';
import { TranslateFunction } from '~/Locale';
import { locale } from '~/config/public/environment';
import { CountryOption } from '~/helpers/countries';
import {
  fieldRuleCity,
  fieldRuleFirstName,
  fieldRuleLastName,
  fieldRuleNationalIdentificationNumberPattern,
  fieldRuleStreetAddress
} from './fieldRules';
import { countries } from '~/App/shared/localization/components/constants';
import { localeToPathSegment } from '~/App/shared/localization/helpers';

/**
 * Language options for the preferred-email-language select, derived from the
 * site language switcher's `countries` list (flags dropped). Values are the
 * IETF language tag (`localeToPathSegment`, e.g. `en-SE` → `en`), deduped so
 * the two "English" locales collapse to a single `en` entry.
 */
export const getLanguageOptions = (): { label: string; value: string }[] => {
  const seen = new Set<string>();

  return countries.reduce<{ label: string; value: string }[]>(
    (options, { value, label }) => {
      const tag = localeToPathSegment(value);
      if (!seen.has(tag)) {
        seen.add(tag);
        options.push({ label, value: tag });
      }
      return options;
    },
    []
  );
};

interface FieldPropsArgs {
  t: TranslateFunction;
  rules?: RegisterOptions;
  name?: string;
  autoComplete?: string;
  required?: boolean;
  country?: string;
}

export const fieldPropsFullName = <FormState extends FieldValues = never>({
  t,
  rules,
  name = 'fullName',
  autoComplete = 'name',
  required = true
}: FieldPropsArgs) => ({
  label: t('Name'),
  rules: {
    required: {
      value: required,
      message: t(`Name can't be blank`)
    },
    ...rules
  },
  name: name as Path<FormState>,
  autoComplete: autoComplete
});

export const fieldPropsPhoneNumber = <FormState extends FieldValues = never>({
  t,
  rules,
  name = 'phoneNumber',
  autoComplete = 'tel',
  required = true
}: FieldPropsArgs) => ({
  label: t('Phone number'),
  rules: {
    required: {
      value: required,
      message: t(`Phone number can't be blank`)
    },
    ...rules
  },
  name: name as Path<FormState>,
  autoComplete: autoComplete
});

export const fieldPropsEmail = <FormState extends FieldValues = never>({
  t,
  rules,
  name = 'email',
  autoComplete = 'email',
  required = true
}: FieldPropsArgs) => ({
  label: t('Email'),
  rules: {
    required: {
      value: required,
      message: t(`Email can't be blank`)
    },
    maxLength: {
      value: EMAIL_MAX_LENGTH,
      message: `${t(
        'Email is too long (maximum %s characters)',
        EMAIL_MAX_LENGTH
      )}`
    },
    pattern: {
      value: regexEmail,
      message: t('Invalid email address')
    },
    ...rules
  },
  name: name as Path<FormState>,
  autoComplete: autoComplete
});

export const fieldPropsLanguage = <FormState extends FieldValues = never>({
  t,
  rules,
  name = 'language',
  autoComplete = 'off',
  required = true
}: FieldPropsArgs) => ({
  label: t('Preferred language'),
  rules: {
    required: {
      value: required,
      message: t(`Preferred language can't be blank`)
    },
    validate: (value: { label: string; value: string }) => {
      return value.value !== '' || t(`Preferred language can't be blank`);
    },
    ...rules
  },
  name: name as Path<FormState>,
  autoComplete: autoComplete,
  options: getLanguageOptions()
});

export const fieldPropsCity = <FormState extends FieldValues = never>({
  t,
  rules,
  name = 'city',
  autoComplete = 'address-level2',
  required = true
}: FieldPropsArgs) => ({
  label: t('City'),
  rules: {
    required: {
      value: required,
      message: t(`City can't be blank`)
    },
    validate: fieldRuleCity(t, required),
    ...rules
  },
  name: name as Path<FormState>,
  autoComplete: autoComplete
});

export const fieldPropsPostalCode = <FormState extends FieldValues = never>({
  t,
  rules,
  name = 'zipCode',
  autoComplete = 'postal-code',
  required = true,
  country = 'se'
}: FieldPropsArgs & { country?: CountryOption['value'] }) => {
  const shouldUseRegexSwedishZipCode = () => {
    if (country && country === 'se') {
      return true;
    }
    return locale === 'sv';
  };

  return {
    label: t('Postal code'),
    rules: {
      required: {
        value: required,
        message: t(`Postal code can't be blank`)
      },
      pattern: {
        value: shouldUseRegexSwedishZipCode()
          ? regexSwedishZipCode
          : regexZipCode,
        message: t('Postal code is invalid')
      },
      ...rules
    },
    name: name as Path<FormState>,
    autoComplete: autoComplete
  };
};

export const fieldPropsAddress = <FormState extends FieldValues = never>({
  t,
  rules,
  name = 'address',
  autoComplete = 'street-address',
  required = true
}: FieldPropsArgs) => ({
  label: t('Address'),
  rules: {
    required: {
      value: required,
      message: t(`Address can't be blank`)
    },
    validate: fieldRuleStreetAddress(t, required),
    ...rules
  },
  name: name as Path<FormState>,
  autoComplete: autoComplete
});

export const fieldPropsFirstName = <FormState extends FieldValues = never>({
  t,
  rules,
  name = 'firstName',
  autoComplete = 'given-name',
  required = true
}: FieldPropsArgs) => ({
  label: t('First name'),
  rules: {
    required: {
      value: required,
      message: t(`First name can't be blank`)
    },
    validate: fieldRuleFirstName(t, required),
    ...rules
  },
  name: name as Path<FormState>,
  autoComplete: autoComplete
});

export const fieldPropsLastName = <FormState extends FieldValues = never>({
  t,
  rules,
  name = 'lastName',
  autoComplete = 'family-name',
  required = true
}: FieldPropsArgs) => ({
  label: t('Last name'),
  rules: {
    required: {
      value: required,
      message: t(`Last name can't be blank`)
    },
    validate: fieldRuleLastName(t, required),
    ...rules
  },
  name: name as Path<FormState>,
  autoComplete: autoComplete
});

export const fieldPropsNationalIdentificationNumber = <
  FormState extends FieldValues = never
>({
  t,
  rules,
  name = 'nationalIdentificationNumber',
  autoComplete = 'off',
  required = true
}: FieldPropsArgs) => ({
  label: t('National identification number'),
  rules: {
    required: {
      value: required,
      message: t(`National identification number can't be blank`)
    },
    maxLength: {
      value: NATIONAL_IDENTIFICATION_NUMBER_MAX_LENGTH,
      message: t(
        'National identification number is too long (maximum is %s characters)',
        NATIONAL_IDENTIFICATION_NUMBER_MAX_LENGTH
      )
    },
    pattern: fieldRuleNationalIdentificationNumberPattern(t),
    ...rules
  },
  name: name as Path<FormState>,
  autoComplete: autoComplete
});

export const fieldPropsOrganisationName = <
  FormState extends FieldValues = never
>({
  t,
  rules,
  name = 'organisationName',
  autoComplete = 'off',
  required = true
}: FieldPropsArgs) => ({
  label: t('Organisation name'),
  rules: {
    required: {
      value: required,
      message: t(`Organisation name can't be blank`)
    },
    maxLength: {
      value: ORGANISATION_NAME_MAX_LENGTH,
      message: t(
        'Organisation name is too long (maximum is %s characters)',
        ORGANISATION_NAME_MAX_LENGTH
      )
    },
    ...rules
  },
  name: name as Path<FormState>,
  autoComplete: autoComplete
});

export const fieldPropsOrganisationNumber = <
  FormState extends FieldValues = never
>({
  t,
  rules,
  name = 'organisationNumber',
  autoComplete = 'off',
  required = true
}: FieldPropsArgs) => ({
  label: t('Organisation number'),
  rules: {
    required: {
      value: required,
      message: t(`Organisation number can't be blank`)
    },
    pattern: {
      value: regexOrganisationNumber,
      message: t('Organisation number is invalid')
    },
    ...rules
  },
  name: name as Path<FormState>,
  autoComplete: autoComplete
});
